import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { Controller, useForm } from 'react-hook-form'
import { Grid } from '@material-ui/core'
import { useWealthboxDomainCommand } from '../../../../../api/wealthbox'
import SydModal from '../../../../commonDesign/SydModal'
import SydModalActions from '../../../../commonDesign/SydModal/SydModalActions'
import SydButton from '../../../../commonDesign/Button'
import SydLabel from '../../../../commonDesign/SydLabel'
import SydTextArea from '../../../../commonDesign/SydTextArea'
import { extractWealthboxErrorMessage } from './wealthboxErrors'

const CREATE_DEFAULTS = {
  content: '',
  tagsCsv: ''
}

function CreateWealthboxNoteModal ({
  open,
  onClose,
  scope = 'client',
  contactId,
  invalidateQueryKeys
}) {
  const [submitError, setSubmitError] = useState(null)
  const createNote = useWealthboxDomainCommand({ invalidateQueryKeys })
  const { control, handleSubmit, reset } = useForm({ defaultValues: CREATE_DEFAULTS })

  useEffect(() => {
    if (open) {
      reset(CREATE_DEFAULTS)
      setSubmitError(null)
    }
  }, [open, reset])

  const submit = handleSubmit(async (values) => {
    setSubmitError(null)
    // Wealthbox POST /v1/notes:
    //   required: `content`
    //   optional: `linked_to: [{id, type}]` (Contact only), `tags` (strings)
    // No `category` field exists. Use `tags` for classification.
    const body = { content: values.content?.trim() }
    const parsedTags = (values.tagsCsv || '').split(',').map(t => t.trim()).filter(Boolean)
    if (parsedTags.length) body.tags = parsedTags
    if (scope === 'client' && contactId != null) {
      body.linked_to = [{ id: Number(contactId), type: 'Contact' }]
    }

    try {
      await createNote.mutateAsync({ command: 'createNote', request: { body, scope } })
      onClose()
    } catch (err) {
      setSubmitError(extractWealthboxErrorMessage(err, 'Failed to create note'))
    }
  })

  return (
    <SydModal open={open} onClose={onClose} title='Create Note' size='xmedium'>
      <form onSubmit={submit}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Controller
              name='content'
              control={control}
              rules={{ required: true }}
              render={({ field }) => (
                <SydLabel label='Note' required>
                  <SydTextArea {...field} value={field.value || ''} rows={6} placeholder='Note content' />
                </SydLabel>
              )}
            />
          </Grid>
          <Grid item xs={12}>
            <Controller
              name='tagsCsv'
              control={control}
              render={({ field }) => (
                <SydLabel
                  label='Tags'
                  description='Optional. Separate multiple tags with commas.'
                >
                  <SydTextArea {...field} value={field.value || ''} rows={1} placeholder='Clients, VIP' />
                </SydLabel>
              )}
            />
          </Grid>
        </Grid>

        {submitError ? (
          <div className='font-size-xs mt-2' style={{ color: 'var(--color-decline-500)' }}>{submitError}</div>
        ) : null}

        <SydModalActions>
          <SydButton variant='ghost' size='sm' onClick={onClose} disabled={createNote.isPending} type='button'>Cancel</SydButton>
          <SydButton variant='primary' size='sm' processing={createNote.isPending} type='submit'>Create Note</SydButton>
        </SydModalActions>
      </form>
    </SydModal>
  )
}

CreateWealthboxNoteModal.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  scope: PropTypes.oneOf(['client', 'user', 'all']),
  contactId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  invalidateQueryKeys: PropTypes.arrayOf(PropTypes.array)
}

export default CreateWealthboxNoteModal
